import SelectField from 'modules/core/components/SelectField'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ContractTaxonomy } from 'modules/contracts/hooks/useFetchContractsTaxonomies'
import useCreateReimbursableSupplementMapping from 'modules/payroll_bundle/hooks/useCreateReimbursableSupplementMapping'
import useDeleteReimbursableSupplementMapping from 'modules/payroll_bundle/hooks/useDeleteReimbursableSupplementMapping'
import { ReimbursableSupplementTaxonomyMapping } from 'modules/payroll_bundle/hooks/useFetchReimbursableSupplementMappings'
import useUpdateReimbursableSupplementMapping from 'modules/payroll_bundle/hooks/useUpdateReimbursableSupplementMapping'

interface Props {
  timeRangeCategoryId: number
  legalEntityId: number
  taxonomies: ContractTaxonomy[]
  mapping: ReimbursableSupplementTaxonomyMapping | undefined
}

const TimeMappingDropdown = ({
  timeRangeCategoryId,
  legalEntityId,
  taxonomies,
  mapping,
}: Props) => {
  const { create: createResultLabelConceptMapping } = useCreateReimbursableSupplementMapping()
  const { update: updateResultLabelConceptMapping } = useUpdateReimbursableSupplementMapping()
  const { delete: deleteResultLabelConceptMapping } = useDeleteReimbursableSupplementMapping()

  const showSyncConfirmation = () => {
    app.setFlash({
      type: 'correct',
      message: i18n.t('settings.payroll_bundle.connected_supplements.time_categories.sync_success'),
    })
  }

  const showErrors = (errors: Record<string, string>) => {
    app.setFlash({
      type: 'wrong',
      message: errors.message || i18n.t('common.unknown'),
    })
  }

  const upsertMapping = async (taxonomyId: string) => {
    if (!taxonomyId) {
      await deleteMapping()
      return
    }

    await (mapping ? updateMapping(taxonomyId) : createMapping(taxonomyId))
  }

  const deleteMapping = async () => {
    if (!mapping) return

    const response = await deleteResultLabelConceptMapping({
      id: mapping.id,
    })

    const result = handleResult(response, (errors) => {
      showErrors(errors)
      return
    })

    if (result.success) showSyncConfirmation()
  }

  const updateMapping = async (taxonomyId: string) => {
    if (!mapping) return

    const response = await updateResultLabelConceptMapping({
      id: mapping.id,
      supplementTaxonomyId: taxonomyId,
    })

    const result = handleResult(response, (errors) => {
      showErrors(errors)
      return
    })

    if (result.success) showSyncConfirmation()
  }

  const createMapping = async (taxonomyId: string) => {
    const response = await createResultLabelConceptMapping({
      legalEntityId: legalEntityId.toString(),
      reimbursableId: timeRangeCategoryId.toString(),
      reimbursableType: 'TimeSettings::CustomTimeRangeCategory',
      supplementTaxonomyId: taxonomyId,
    })

    const result = handleResult(response, (errors) => {
      showErrors(errors)
      return
    })

    if (result.success) showSyncConfirmation()
  }

  const options = taxonomies.map((taxonomy) => ({
    label: taxonomy.name,
    value: taxonomy.id,
  }))

  const selectedValue = mapping?.supplementTaxonomyId ?? ''

  return (
    <SelectField
      options={options}
      none={i18n.t('common.none')}
      onChange={upsertMapping}
      value={selectedValue}
    />
  )
}

export default TimeMappingDropdown
